'use client';

import React from 'react';

import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {generateTrackingId, useTracker} from '@udemy-v2/event-tracking';
import {BackendSourceOptions} from '@udemy-v2/react-browse-event-tracking';
import {
    ProfessionalSkillsUnit as SharedProfessionalSkillsUnit,
    withActiveSectionData,
} from '@udemy-v2/react-discovery-units';
import {useFeatures} from '@udemy-v2/react-experimentation';
import {
    LoadingStatus,
    RecommendationsProvider,
    useRecommendationsSections,
} from '@udemy-v2/react-recommendations';

import {LOHP_CAROUSEL_HOVER_POPUP_REMOVAL} from '@/app/lib/experimentation';
import {createClickEvent, createDiscoveryItemClickEvent} from '../../data/tracking';
import {extractCourses, getContextInput, type PsuTab} from './professional-skills-unit.utils';

import type {ProfessionalSkillsSection, RenderLinkProps} from '@udemy-v2/react-discovery-units';

interface ProfessionalSkillsUnitProps {
    tabs: PsuTab[] | null;
}

const PsuSections: React.FC<{
    tabs: PsuTab[];
    activeIndex: number;
    onActiveIndexChange: (index: number) => void;
}> = ({tabs, activeIndex, onActiveIndexChange}) => {
    const tracker = useTracker();
    const {data, status} = useRecommendationsSections();
    const isLoading = status === LoadingStatus.LOADING || status === LoadingStatus.IDLE;
    const courses = status === LoadingStatus.LOADED ? extractCourses(data) : [];
    const isMobileMax = useMatchMedia('xs-max');
    const {featureVariantAssignments} = useFeatures([LOHP_CAROUSEL_HOVER_POPUP_REMOVAL]);
    // TODO: clean up once lohp_carousel_hover_popup_removal experiment concludes
    const isHoverPopupRemovalEnabled = Boolean(
        featureVariantAssignments.get(LOHP_CAROUSEL_HOVER_POPUP_REMOVAL)?.configuration?.enabled,
    );
    const showInlineCta = !isMobileMax && isHoverPopupRemovalEnabled;

    const baseSections = tabs.map((tab, index) => ({
        id: tab.name,
        name: tab.name,
        index,
        showAllHref: tab.topicUrl,
    }));
    const sections = withActiveSectionData(baseSections, activeIndex, {courses, isLoading}); // only active courses are fetched at a time

    const handleSectionChange = (section: ProfessionalSkillsSection) => {
        const tab = tabs[section.index];
        if (!tab) return;

        onActiveIndexChange(section.index);
        tracker.publishEvent(
            createDiscoveryItemClickEvent({
                id: tab.labelId,
                type: 'course_topic',
                trackingId: generateTrackingId(),
                serveTrackingId: String(tab.labelId),
                backendSource: BackendSourceOptions.DISCOVERY,
                position: section.index + 1,
                uiRegion: 'skills_to_transform',
            }),
        );
    };

    const renderLink = ({href, className, children}: RenderLinkProps) => (
        <a
            href={href}
            className={className}
            onClick={() => {
                const tab = tabs.find((t) => t.topicUrl === href);
                if (!tab) return;
                tracker.publishEvent(
                    createClickEvent({
                        componentName: 'browseMoreCourses',
                        relatedObjectType: 'course_topic',
                        relatedObjectId: tab.labelId,
                    }),
                );
            }}
        >
            {children}
        </a>
    );

    return (
        <SharedProfessionalSkillsUnit
            sections={sections}
            showQuickView={!showInlineCta}
            showAddToCartInPopover={!showInlineCta}
            showStateAwareAddToCartInCourseCards={showInlineCta}
            onSectionChange={handleSectionChange}
            renderLink={renderLink}
        />
    );
};

export const ProfessionalSkillsUnit: React.FC<ProfessionalSkillsUnitProps> = ({tabs}) => {
    const [activeIndex, setActiveIndex] = React.useState(0);

    if (!tabs?.length) return null;

    const resolvedIndex = activeIndex < tabs.length ? activeIndex : 0;

    return (
        <RecommendationsProvider
            includeAvailableSubscriptionInfo={true}
            key={tabs.map((tab) => tab.name).join('|')}
            contextInput={getContextInput(tabs[resolvedIndex].labelId)}
        >
            <PsuSections
                tabs={tabs}
                activeIndex={resolvedIndex}
                onActiveIndexChange={setActiveIndex}
            />
        </RecommendationsProvider>
    );
};
